import { OBJECT_TYPE_CONTAINER } from "@/data/constants";
import { iconObjectToIconString } from "@/helper/icons";
import type {
  ApiObjectWithReferences,
  FontAwesomeIcon,
  IconName,
  IconSet,
  IconStyle,
} from "@/models/api";

/** Fewest icons to prefetch per page, whatever the scroller shows at once. */
export const MIN_PREFETCH_PAGE_SIZE = 8;
/** How many pages after the visible ones are prefetched. */
export const PREFETCH_PAGES = 2;

/**
 * The items after the visible range `[start, end)` which should be prefetched:
 * the next `PREFETCH_PAGES` pages, a page being as many items as are visible (at least `MIN_PREFETCH_PAGE_SIZE`).
 */
export function nextItemsToPrefetch<T>(
  items: readonly T[],
  start: number,
  end: number
): T[] {
  const pageSize = Math.max(end - start, MIN_PREFETCH_PAGE_SIZE);
  return items.slice(
    Math.max(end, 0),
    Math.max(end, 0) + PREFETCH_PAGES * pageSize
  );
}

/** Most icons prefetched for the levels around the tree view, so a huge level cannot flood the batch endpoint. */
export const MAX_TREE_PREFETCH_ICONS = 48;

export type PrefetchIcon = { type: IconSet; name: IconName; style: IconStyle };

/** What the tree view needs to look around: an object with its children and parent, and the objects at the root. */
export type TreeLookup = {
  byId: (
    id: number
  ) => {
    data: ApiObjectWithReferences;
    children: ApiObjectWithReferences[];
    parentID: number | null;
  } | null;
  rootObjects: () => ApiObjectWithReferences[];
};

/**
 * The logos of the levels around the container `currentId` (`null` is the root) which are not on screen yet:
 * first the next child level (the children of its containers), then the parent level (the parent and its siblings).
 * Objects with an image are left out, their logo is only the fallback.
 * Unique by icon, at most `MAX_TREE_PREFETCH_ICONS`.
 */
export function treeLevelIconsToPrefetch(
  currentId: number | null,
  lookup: TreeLookup
): PrefetchIcon[] {
  const current = currentId === null ? null : lookup.byId(currentId);
  const currentLevel =
    current === null ? lookup.rootObjects() : current.children;

  const childLevel = currentLevel
    .filter((child) => child.object.type === OBJECT_TYPE_CONTAINER)
    .flatMap((child) => lookup.byId(child.object.id)?.children ?? []);

  let parentLevel: ApiObjectWithReferences[] = [];
  if (current !== null) {
    const parent =
      current.parentID === null ? null : lookup.byId(current.parentID);
    parentLevel = [
      ...(parent === null ? [] : [parent.data]),
      ...(parent === null ? lookup.rootObjects() : parent.children),
    ];
  }

  const icons = new Map<string, PrefetchIcon>();
  for (const { object, images } of [...childLevel, ...parentLevel]) {
    if (icons.size >= MAX_TREE_PREFETCH_ICONS) {
      break;
    }
    if (!object.logo || images.length > 0) {
      continue;
    }
    const logo = object.logo as FontAwesomeIcon;
    icons.set(iconObjectToIconString(logo), {
      type: logo.type,
      name: logo.name,
      style: logo.style,
    });
  }
  return [...icons.values()];
}
